/**
 * AnalyticsDataService
 *
 * Queries real Firestore data for the Analytics dashboard.
 * Pulls from: appointments, patients, clinical_notes, form_responses
 */

import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../config/firebase';

export interface DateRange {
  start: Date;
  end: Date;
}

export interface AppointmentMetrics {
  total: number;
  completed: number;
  cancelled: number;
  noShow: number;
  scheduled: number;
  confirmed: number;
  byModality: { video: number; inPerson: number };
  bySessionType: Record<string, number>;
}

export interface ClientMetrics {
  totalActive: number;
  totalInactive: number;
  newThisPeriod: number;
  byInsuranceType: { insurance: number; selfPay: number; unknown: number };
}

export interface NotesMetrics {
  total: number;
  draft: number;
  final: number;
  signed: number;
  aiAssisted: number;
  byTemplate: Record<string, number>;
}

export interface DashboardData {
  appointments: AppointmentMetrics;
  clients: ClientMetrics;
  notes: NotesMetrics;
  projected: { appointments: number; clients: number };
}

/**
 * Get date range for a given time period filter.
 */
export function getDateRange(period: string): DateRange {
  const now = new Date();
  const start = new Date();
  const end = new Date();

  switch (period) {
    case 'this-month':
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'last-30-days':
      start.setDate(now.getDate() - 30);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      break;
    case 'last-month':
      start.setMonth(now.getMonth() - 1, 1);
      start.setHours(0, 0, 0, 0);
      end.setDate(0); // last day of previous month
      end.setHours(23, 59, 59, 999);
      break;
    case 'this-year':
      start.setMonth(0, 1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(11, 31);
      end.setHours(23, 59, 59, 999);
      break;
    default:
      // Default to this month
      start.setDate(1);
      start.setHours(0, 0, 0, 0);
      end.setMonth(end.getMonth() + 1, 0);
      end.setHours(23, 59, 59, 999);
  }

  return { start, end };
}

class AnalyticsDataService {
  private static instance: AnalyticsDataService;

  static getInstance(): AnalyticsDataService {
    if (!AnalyticsDataService.instance) {
      AnalyticsDataService.instance = new AnalyticsDataService();
    }
    return AnalyticsDataService.instance;
  }

  /**
   * Fetch appointment metrics for a provider within a date range.
   */
  async getAppointmentMetrics(providerId: string, dateRange: DateRange): Promise<AppointmentMetrics> {
    try {
      const q = query(
        collection(db, 'appointments'),
        where('providerId', '==', providerId),
        where('appointmentDate', '>=', Timestamp.fromDate(dateRange.start)),
        where('appointmentDate', '<=', Timestamp.fromDate(dateRange.end)),
        orderBy('appointmentDate', 'asc')
      );

      const snapshot = await getDocs(q);
      const metrics: AppointmentMetrics = {
        total: 0,
        completed: 0,
        cancelled: 0,
        noShow: 0,
        scheduled: 0,
        confirmed: 0,
        byModality: { video: 0, inPerson: 0 },
        bySessionType: {},
      };

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        metrics.total++;

        // Status breakdown
        switch (data.status) {
          case 'completed': metrics.completed++; break;
          case 'cancelled': metrics.cancelled++; break;
          case 'no-show': metrics.noShow++; break;
          case 'scheduled': metrics.scheduled++; break;
          case 'confirmed': metrics.confirmed++; break;
        }

        // Modality
        if (data.modality === 'video') metrics.byModality.video++;
        else metrics.byModality.inPerson++;

        // Session type
        const st = data.sessionType || 'other';
        metrics.bySessionType[st] = (metrics.bySessionType[st] || 0) + 1;
      });

      return metrics;
    } catch (error) {
      console.error('[AnalyticsDataService] Error fetching appointments:', error);
      return {
        total: 0, completed: 0, cancelled: 0, noShow: 0, scheduled: 0, confirmed: 0,
        byModality: { video: 0, inPerson: 0 }, bySessionType: {},
      };
    }
  }

  /**
   * Fetch client metrics for a provider.
   */
  async getClientMetrics(providerId: string, dateRange: DateRange): Promise<ClientMetrics> {
    try {
      // Get all patients for this provider
      const q = query(
        collection(db, 'patients'),
        where('assignedProviderId', '==', providerId)
      );
      const snapshot = await getDocs(q);

      const metrics: ClientMetrics = {
        totalActive: 0,
        totalInactive: 0,
        newThisPeriod: 0,
        byInsuranceType: { insurance: 0, selfPay: 0, unknown: 0 },
      };

      snapshot.docs.forEach(doc => {
        const data = doc.data();

        // Status
        if (data.status === 'active') metrics.totalActive++;
        else if (data.status === 'inactive') metrics.totalInactive++;

        // New clients in period
        if (data.createdAt) {
          const created = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          if (created >= dateRange.start && created <= dateRange.end) {
            metrics.newThisPeriod++;
          }
        }

        // Insurance breakdown
        if (data.insurance?.provider || data.insurance?.policyNumber) {
          metrics.byInsuranceType.insurance++;
        } else if (data.billingType === 'Self-pay') {
          metrics.byInsuranceType.selfPay++;
        } else {
          metrics.byInsuranceType.unknown++;
        }
      });

      return metrics;
    } catch (error) {
      console.error('[AnalyticsDataService] Error fetching clients:', error);
      return { totalActive: 0, totalInactive: 0, newThisPeriod: 0, byInsuranceType: { insurance: 0, selfPay: 0, unknown: 0 } };
    }
  }

  /**
   * Fetch clinical notes metrics for a provider within a date range.
   */
  async getNotesMetrics(providerId: string, dateRange: DateRange): Promise<NotesMetrics> {
    try {
      const q = query(
        collection(db, 'clinical_notes'),
        where('providerId', '==', providerId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);

      const metrics: NotesMetrics = {
        total: 0,
        draft: 0,
        final: 0,
        signed: 0,
        aiAssisted: 0,
        byTemplate: {},
      };

      snapshot.docs.forEach(doc => {
        const data = doc.data();

        // Filter by date range
        if (data.createdAt) {
          const created = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
          if (created < dateRange.start || created > dateRange.end) return;
        }

        metrics.total++;

        // Status
        switch (data.status) {
          case 'draft': metrics.draft++; break;
          case 'final': metrics.final++; break;
          case 'signed': metrics.signed++; break;
        }

        // AI assisted
        if (data.wasAiAssisted) metrics.aiAssisted++;

        // Template type
        const template = data.template || 'Unknown';
        metrics.byTemplate[template] = (metrics.byTemplate[template] || 0) + 1;
      });

      return metrics;
    } catch (error) {
      console.error('[AnalyticsDataService] Error fetching notes:', error);
      return { total: 0, draft: 0, final: 0, signed: 0, aiAssisted: 0, byTemplate: {} };
    }
  }

  /**
   * Get projected stats (upcoming scheduled appointments and active clients).
   */
  async getProjectedStats(providerId: string): Promise<{ appointments: number; clients: number }> {
    try {
      const now = new Date();
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const q = query(
        collection(db, 'appointments'),
        where('providerId', '==', providerId),
        where('appointmentDate', '>=', Timestamp.fromDate(now)),
        where('appointmentDate', '<=', Timestamp.fromDate(endOfMonth)),
        orderBy('appointmentDate', 'asc')
      );
      const snapshot = await getDocs(q);

      const upcomingAppointments = snapshot.docs.filter(doc => {
        const status = doc.data().status;
        return status === 'scheduled' || status === 'confirmed';
      }).length;

      const uniqueClients = new Set(
        snapshot.docs.map(doc => doc.data().patientId).filter(Boolean)
      );

      return { appointments: upcomingAppointments, clients: uniqueClients.size };
    } catch (error) {
      console.error('[AnalyticsDataService] Error fetching projected:', error);
      return { appointments: 0, clients: 0 };
    }
  }

  /**
   * Fetch all dashboard data in parallel.
   */
  async getDashboardData(providerId: string, period: string): Promise<DashboardData> {
    const dateRange = getDateRange(period);

    const [appointments, clients, notes, projected] = await Promise.all([
      this.getAppointmentMetrics(providerId, dateRange),
      this.getClientMetrics(providerId, dateRange),
      this.getNotesMetrics(providerId, dateRange),
      this.getProjectedStats(providerId),
    ]);

    return { appointments, clients, notes, projected };
  }
}

export default AnalyticsDataService;
